<template>
  <div>

    <!-- OM MIG -->
    <section id="om-mig" class="bg-cream pt-6 pb-10 md:pt-12 md:pb-20">
      <div class="max-w-6xl mx-auto px-6">

        <div class="mb-8 md:mb-12">
          <p class="section-label mb-4">Om mig</p>
          <div class="divider-line mb-6"></div>
          <h2 class="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-charcoal leading-tight">
            Carina Landin
          </h2>
        </div>

        <div class="flex flex-col lg:flex-row gap-8 lg:gap-16">

          <div class="flex-1">
            <p class="text-muted font-light leading-relaxed mb-5">
              Orden "normalt" och "lagom" kan gärna strykas ur det svenska språket om du frågar mig.
              I min värld finns det inget som är märkligt, konstigt eller fel.
            </p>
            <p class="text-muted font-light leading-relaxed">
              Jag är en social ensamvarg som gillar att bygga saker. Jag tycker om fakta, men är inte en teoretiker.
              Vi är alla unika individer och det är min utgångspunkt i allt jag gör.
              En bra dag på jobbet är när jag kan hjälpa någon att se något de redan vet men från ett annat perspektiv.
            </p>
          </div>

          <div class="flex-1 flex flex-col gap-6 justify-between">
            <blockquote class="font-serif text-2xl md:text-3xl font-light text-charcoal leading-snug border-l-2 pl-6" style="border-color: var(--color-terra);">
              "Jag har än idag inte använt mig av en mall i enskilda terapier då det inte finns en kopia av dig."
            </blockquote>
          </div>

        </div>
      </div>
    </section>

    <!-- TJÄNSTER -->
    <section id="tjanster" class="bg-sand py-6 md:py-12">
      <div class="max-w-6xl mx-auto px-6">
        <div class="text-center mb-8 md:mb-14">
          <p class="section-label mb-4">Tjänster</p>
          <div class="divider-line-center mb-6"></div>
          <h2 class="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-charcoal">Hur jag kan hjälpa dig</h2>
        </div>

        <div class="grid md:grid-cols-2 gap-6 md:gap-8">

          <div class="card p-6 md:p-10">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center" style="background: rgba(138,158,140,0.15);">
                <span style="color: var(--color-sage-dk); font-size: 1.2rem;">✦</span>
              </div>
              <h3 class="font-serif text-3xl font-light text-charcoal">Samtal</h3>
            </div>
            <p class="text-muted font-light leading-relaxed mb-4">
              Trygghet ges i att få växa och bearbeta hämmande mönster, livshändelser samt trauman.
              En känsla av att du är sedd och hållen till en verklig förändring i ditt liv.
            </p>
            <p class="text-muted font-light leading-relaxed mb-4">
              Du ökar din medvetenhet och får stöd i att lyssna till det som diffust pågår i vardagen eller orsakar negativa sinnestillstånd.
            </p>
            <p class="text-muted font-light leading-relaxed mb-4">
              Vi använder samtal, andning, tapping, havening samt holoptropiskt tillstånd vid behov.
            </p>
            <p class="text-muted font-light leading-relaxed mb-6">
              En ökad förmåga att relatera till dig själv och andra genom ett expanderat livsrum som förstärker känslan av att stå i din sanning.
            </p>
            <p class="text-muted font-light leading-relaxed mb-6">
              Jag har erfarenhet av stödjande/integrerande samtal efter transpersonella tillstånd och psykedeliska upplevelser.
            </p>

          </div>

          <div class="card p-6 md:p-10">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center" style="background: rgba(196,115,90,0.12);">
                <span style="color: var(--color-terra); font-size: 1.2rem;">◎</span>
              </div>
              <h3 class="font-serif text-3xl font-light text-charcoal">Fysiskt möte eller videosession</h3>
            </div>
            <p class="text-muted font-light leading-relaxed mb-4">
              Jag erbjuder samtalsterapi i Stockholm och har mottagning med lugnt och centralt läge på Fridhemsplan.
            </p>
            <p class="text-muted font-light leading-relaxed mb-4">
              Jag arbetar även med videosessioner med personer runt om i hela landet.
              Det är helt upp till dig vilken mötesform du känner dig mest bekväm med.
            </p>
            <p class="text-muted font-light leading-relaxed mb-8">
              Hur många sessioner som behövs är helt individuellt och olika för alla.
              För vissa räcker 2–3 sessioner, andra vill gå regelbundet under en längre tid för kontinuerlig självutveckling.
            </p>
            <div class="bg-cream rounded p-5">
              <p class="text-charcoal font-medium text-lg mb-1">1 250 kr <span class="text-sm font-light text-muted">/ 45 min</span></p>
              <p class="text-xs text-muted font-light">Avbokning skall ske 24 timmar innan</p>
            </div>
            <div class="mt-8">
              <a href="mailto:carina@carinalandin.se" class="btn-primary">Kontakta mig</a>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- UTBILDNINGAR -->
    <section id="utbildning" class="bg-sand py-6 md:py-12 overflow-hidden">
      <div class="max-w-6xl mx-auto px-6">

        <div class="text-center mb-8 md:mb-14">
          <p class="section-label mb-4">Bakgrund</p>
          <div class="divider-line-center mb-6"></div>
          <h2 class="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-charcoal">Utbildningar</h2>
        </div>

        <div class="edu-grid">
          <div
            v-for="(edu, i) in educations"
            :key="i"
            class="edu-card"
            :class="'edu-card--' + (i % 3)"
          >
            <p class="edu-title-back">{{ edu.title }}</p>
            <div class="edu-divider"></div>
            <p v-if="edu.duration" class="edu-duration">{{ edu.duration }}</p>
            <p class="edu-desc">{{ edu.desc }}</p>
          </div>
        </div>

      </div>
    </section>

    <!-- PODCAST -->
    <section id="podcast" class="bg-sand py-6 md:py-12">
      <div class="max-w-6xl mx-auto px-6">
        <div class="flex flex-col lg:flex-row gap-6 lg:gap-20 items-center">

          <div class="flex-1">
            <p class="section-label mb-4">Media</p>
            <div class="divider-line mb-6 md:mb-8"></div>
            <h2 class="font-serif text-4xl md:text-5xl font-light text-charcoal mb-5 md:mb-6">Klimakteriepodden</h2>
            <p class="text-muted font-light leading-relaxed mb-8">
              Jag fick äran att vara med i ett avsnitt av
              <a href="https://www.klimakteriepodden.se/" class="text-terra hover:underline transition-colors" target="_blank" rel="noopener">Klimakteriepodden</a>
              där vi diskuterade hur vi kan hantera oro och ångest.
            </p>
            <a href="https://aca.st/45c475" target="_blank" rel="noopener" class="btn-outline">Lyssna på avsnittet</a>
          </div>

          <div class="flex-shrink-0 w-48 sm:w-64 lg:w-80 mx-auto lg:mx-0">
            <a href="https://aca.st/45c475" target="_blank" rel="noopener" class="block group">
              <div class="relative overflow-hidden rounded-2xl shadow-md">
                <img
                  src="@/assets/klimakteriepodden.jpg"
                  alt="Klimakteriepodden"
                  class="w-full transition-transform duration-500 group-hover:scale-105"
                />
                <div class="absolute inset-0 bg-charcoal opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl"></div>
              </div>
            </a>
          </div>

        </div>
      </div>
    </section>

    <!-- CONTACT FOOTER -->
    <section class="py-6 md:py-10" style="background: var(--color-charcoal);">
      <div class="max-w-6xl mx-auto px-6 text-center">
        <p class="section-label mb-4" style="color: rgba(255,255,255,0.4);">Kontakt</p>
        <h2 class="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6">Redo för nästa steg?</h2>
        <p class="text-white opacity-50 font-light mb-10 max-w-md mx-auto">
          Välkommen att höra av dig. Jag svarar så snart jag kan.
        </p>
        <a href="mailto:carina@carinalandin.se" class="btn-primary">carina@carinalandin.se</a>
        <p class="mt-8 text-xs tracking-widest uppercase opacity-30 text-white">Stockholm</p>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  data() {
    return {
      educations: [
        {
          title: 'Cert. Grof® Breathwork and Psychedelic Therapy',
          duration: '3 år',
          icon: '🌬️',
          desc: 'Certifiering i Grof® Breathwork – en djupgående metod som kombinerar andningsteknik och psykedelisk integration.',
        },
        {
          title: 'Transpersonell terapeut',
          duration: '3 år',
          icon: '✦',
          desc: 'Treårig utbildning grundad i Dr. Stanislav Grofs transpersonella psykologi.',
        },
        {
          title: 'KBT coach och schematerapi',
          duration: '2 år',
          icon: '◎',
          desc: 'Kognitiv beteendeterapi och schematerapi för att identifiera och förändra hämmande mönster.',
        },
        {
          title: 'Leg. Arbetsterapeut',
          duration: '3 år',
          icon: '⬡',
          desc: 'Legitimerad arbetsterapeut med fokus på aktivitet, vardagsliv och hälsa.',
        },
        {
          title: 'Acceptance and Commitment Therapy',
          duration: null,
          icon: '○',
          desc: 'ACT – att skapa ett rikt och meningsfullt liv genom psykologisk flexibilitet.',
        },
        {
          title: 'Diplomerad mindfulnessinstruktör',
          duration: null,
          icon: '◈',
          desc: 'Utbildad att handleda i mindfulness och närvaro som verktyg för välmående.',
        },
      ],
    };
  },
};
</script>

<style scoped>
.edu-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
}

@media (max-width: 768px) {
  .edu-grid { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 480px) {
  .edu-grid { grid-template-columns: 1fr; }
}

.edu-card {
  border-radius: 6px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 180px;
  background-color: #B85C3C;
}

@media (min-width: 640px) {
  .edu-card { padding: 2rem; min-height: 220px; }
}

.edu-card--1 { background-color: #5E7560; }
.edu-card--2 { background-color: #8A6A50; }

.edu-title-back {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.3rem;
  font-weight: 400;
  color: rgba(255,255,255,0.95);
  line-height: 1.3;
  margin-bottom: 0.75rem;
}

.edu-divider {
  width: 32px;
  height: 1px;
  background: rgba(255,255,255,0.4);
  margin-bottom: 0.75rem;
}

.edu-duration {
  font-family: 'Inter', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.6);
  margin-bottom: 0.75rem;
}

.edu-desc {
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
  font-weight: 300;
  color: rgba(255,255,255,0.85);
  line-height: 1.6;
}
</style>
